/* eslint-disable no-unused-vars */
import { Emittable } from '@/data/productlist/generic.ts';
import { reactive, computed, type Ref } from 'vue';
import { formatWeight } from '@/data/api/products/helpers';

import _ from 'lodash';
import { Currency, PriceManager, PriceType, Pricing } from '@/helpers/pricemanager';

export interface TotalsWrapper {
  currency: string;
  price: {
    price: number;
    tax: number;
    total: number;
  };
  formatted: {
    price: string;
    tax: string;
    total: string;
  };
}

/* Totals module
  Requires:
  - Articles
  - Price (For price totals)
*/
export class Totals extends Emittable {
  composable: ({}: {
    rows: Ref<any[]>;
    currency?: Ref<string>;
    price_managers?: Ref<Record<string, PriceManager>>;
  }) => {
    p_c_total_weight: any;
    p_c_total_price: any;
  };
  constructor() {
    super();
    this.composable = ({
      rows,
      currency,
      price_managers,
    }: {
      rows: Ref<any[]>;
      currency?: Ref<string>;
      price_managers?: Ref<Record<string, PriceManager>>;
    }) => {
      const state = reactive({
        p_c_total_weight: computed(() => {
          let total = rows.value?.reduce((sum, r) => {
            sum += parseFloat(r.weight) || 0;
            return sum;
          }, 0);

          total = total?.toFixed(2);

          return { raw: !isNaN(total) ? total : 0, formatted: !isNaN(total) ? formatWeight(total) : '...' };
        }),
        p_c_total_price: computed(() => {
          const VAT = 0.25;
          let sum = new Pricing(0, currency?.value ?? Currency.SEK);

          for (const row of rows.value ?? []) {
            const price = price_managers?.[row.id] as PriceManager | undefined;
            if (!price) {
              console.warn('No price manager found for row', row.id);
              continue;
            }

            let total: Pricing;
            total = price.getPriceTotal(PriceType.Net);
            sum = sum.add(total) ?? sum;
          }

          return {
            currency: currency?.value, // Fetched from price module
            price: {
              price: sum.value ?? 0,
              tax: sum.multiply(VAT)?.value ?? 0,
              total: sum.multiply(1 + VAT)?.value ?? 0,
            },
            formatted: {
              price: sum.toString(),
              tax: sum.multiply(VAT)?.toString() ?? '',
              total: sum.multiply(1 + VAT)?.toString() ?? '',
            },
          } as TotalsWrapper;
        }),
      });

      return state;
    };
    this.mixin = {
      data() {
        return {};
      },
      computed: {
        p_c_total_weight() {
          let total = this.p_c_rows?.reduce((sum, r) => {
            sum += parseFloat(r.weight) || 0;
            return sum;
          }, 0);

          total = total?.toFixed(2);

          return { raw: !isNaN(total) ? parseFloat(total) : 0, formatted: !isNaN(total) ? formatWeight(total) : '...' };
        },
        p_c_total_price() {
          const VAT = 0.25;
          let sum = new Pricing(0, this.c_price_currency);

          for (const row of this.p_c_rows ?? []) {
            const price = this.c_price_managers[row.id] as PriceManager | undefined;
            if (!price) {
              console.warn('No price manager found for row', row.id);
              continue;
            }

            let total: Pricing;
            total = price.getPriceTotal(PriceType.Net);
            sum = sum.add(total) ?? sum;
          }

          return {
            currency: this.c_price_currency, // Fetched from price module
            price: {
              price: sum.value ?? 0,
              tax: sum.multiply(VAT)?.value ?? 0,
              total: sum.multiply(1 + VAT)?.value ?? 0,
            },
            formatted: {
              price: sum.toString(),
              tax: sum.multiply(VAT)?.toString() ?? '',
              total: sum.multiply(1 + VAT)?.toString() ?? '',
            },
          } as TotalsWrapper;
        },
      },
    };
  }
}
